import { useEffect, useState } from "react"
import { captureMessage } from "@sentry/react"

import PurchaseModal from "app/components/subscriptions/purchase_modal"
import Modal from "shared/components/modal"
import closeX from "images/components/x.svg"
import { loadState, saveState } from "shared/localstorage_helper"
import moment from "moment"

import "../checkout_modal/index.scss"

interface URLs {
  learnMore: string
  modalSubscriptionUrl: string
  newProPlusSubscriptionUrl: string
  newProSubscriptionUrl: string
}

interface SubscriptionProps {
  assets: { iftttSvgUrl: string; proBadgeSvgUrl: string; proPlusBadgeSvgUrl: string }
  browser_type: string
  create_stripe_customer_url: string
  eligible_for_trial: boolean
  email: string
  initial_promo_code: string | null
  intermediate_pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  subscriptions_path: string
}

interface Props {
  checkoutModalTarget: string
  onFinish: () => void
  twitterMigration: boolean
  urls: URLs
}

const AlertCheckout = ({ checkoutModalTarget, onFinish, twitterMigration, urls }: Props) => {
  const [modalVisible, setModalVisibility]: [boolean, (a: boolean) => void] = useState(true)
  const [showCheckout, setShowCheckout]: [boolean, (a: boolean) => void] = useState(false)
  const [subscriptionProps, updateSubscriptionProps]: [SubscriptionProps, (a: SubscriptionProps) => void] =
    useState<SubscriptionProps>({} as SubscriptionProps)
  const [subscriptionPropsLoaded, setSubscriptionPropsLoaded]: [boolean, (a: boolean) => void] = useState(false)

  const planTarget = checkoutModalTarget === "pro" ? "Pro+" : "Pro"
  const title = `Upgrade to ${planTarget}`
  const copy = "Avoid interruption and continue enjoying your existing Applets."

  const onClose = () => {
    fireGAEvent("click_close")
    setModalVisibility(false)
    setShowCheckout(false)
    onFinish?.()
  }
  const onUpgrade = () => {
    onFinish?.()
    window.appendFlash(`Upgrade complete. Welcome to IFTTT ${planTarget}!`, "success", () => window.location.reload())
  }

  const checkoutStorageId = `checkoutModalSubscriptionProps-${window.App.userId}`
  const loadCachedSubscriptionProps = () => JSON.parse(loadState(checkoutStorageId) || "null")
  const cacheSubscriptionProps = data => {
    saveState(
      JSON.stringify({
        ttl: moment().add("minutes", 10).unix(),
        data,
      }),
      checkoutStorageId
    )
  }

  const fireGAEvent = (step: string) => {
    window.ga && window.ga("gtm2.send", "event", "TierModification", step)
  }

  const loadSubscriptionProps = () => {
    const cachedSubscriptionProps = loadCachedSubscriptionProps()

    if (cachedSubscriptionProps && moment().unix() < cachedSubscriptionProps.ttl) {
      if (window.App.env && window.App.allowDebugErrorMessages) {
        console.log("subscriptionProps loaded from cache", cachedSubscriptionProps)
      }
      updateSubscriptionProps(cachedSubscriptionProps.data)
      setSubscriptionPropsLoaded(true)
      return
    }

    fetch(urls.modalSubscriptionUrl, {
      method: "get",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "error",
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status)
        }

        return response.json()
      })
      .then(result => {
        updateSubscriptionProps(result)
        setSubscriptionPropsLoaded(true)
        cacheSubscriptionProps(result)
      })
      .catch(() => {
        // In case that we have an issue load data to be used in SignUpAndPurchase, not block user. Redirect them to checkout page.
        captureMessage("Checkout modal failed to load subscription data and redirect user to checkout page.", {
          tags: { user_id: window.App.userId },
          extra: { checkoutModalTarget },
        })
      })
  }

  useEffect(() => {
    loadSubscriptionProps()
    fireGAEvent("show_communication")
  }, [])

  useEffect(() => {
    if (modalVisible) {
      if (document.querySelectorAll("meta[name='turbolinks-cache-control']").length > 0) return

      const meta = document.createElement("meta")
      meta.name = "turbolinks-cache-control"
      meta.content = "no-cache"

      document.getElementsByTagName("head")[0].appendChild(meta)
    }
  }, [modalVisible])

  const illustrationClass = twitterMigration ? "illustration-legacy-twitter" : "illustration-legacy-default"

  const checkDataAndShowCheckout = () => {
    fireGAEvent("click_upgrade")
    if (subscriptionPropsLoaded) {
      setShowCheckout(true)
    } else {
      window.location.assign(
        checkoutModalTarget === "intermediate_pro" ? urls.newProSubscriptionUrl : urls.newProPlusSubscriptionUrl
      )
    }
  }

  return (
    <div styleName="modal-container checkout-on-modal" className="checkout-on-modal">
      <Modal show={modalVisible} includeCloseX={false}>
        <section styleName="checkout-container free-tier-migration">
          <section styleName="left" className={illustrationClass} />
          <section styleName="right">
            <div styleName="close" onClick={onClose}>
              <span
                className="close-x"
                dangerouslySetInnerHTML={{
                  __html: closeX,
                }}
              />
            </div>
            {showCheckout ? (
              <>
                <header styleName="onboarding-header" className="pb2 pb0--phone">
                  <h1 styleName="ifttt">
                    <img src={subscriptionProps["assets"].iftttSvgUrl} alt="IFTTT logo" width="122" height="37" />
                    {checkoutModalTarget === "pro" ? (
                      <img
                        src={subscriptionProps["assets"].proPlusBadgeSvgUrl}
                        alt="Pro+ badge"
                        width="91"
                        height="53"
                      />
                    ) : (
                      <img src={subscriptionProps["assets"].proBadgeSvgUrl} alt="Pro badge" width="91" height="53" />
                    )}
                  </h1>
                  <h2 className="alt-title" styleName="modal-title">
                    {title}
                  </h2>
                  <p className="copy txt-body-3">
                    {copy} {subscriptionProps["eligible_for_trial"] && `You can trial ${planTarget} for 7 days.`}
                  </p>
                </header>
                <PurchaseModal
                  onClose={onClose}
                  onUpgrade={onUpgrade}
                  subscriptionProps={subscriptionProps}
                  target={checkoutModalTarget}
                />
              </>
            ) : (
              <>
                <header styleName="onboarding-header" className="pb2 pb0--phone">
                  <h2 className="alt-title" styleName="modal-title align-left">
                    Updates to your account
                  </h2>
                  <p className="txt-body-3">
                    Starting May 23, free users will be:
                    <ul className="txt-body-3">
                      <li>Limited to two Applets.</li>
                      <li>Unable to access the Twitter service or use Twitter Applets.</li>
                    </ul>
                  </p>
                  <p className="txt-body-3">
                    {`Go ${planTarget} to avoid interruption and continue enjoying your existing Applets.`}
                  </p>
                </header>
                <div className="flexbox" styleName="flexbox">
                  <section className="action-buttons-container">
                    <button className="button-secondary" onClick={checkDataAndShowCheckout}>Upgrade</button>
                    <a className="button-tertiary button-cancel-link" href={urls.learnMore}>Learn more</a>
                  </section>
                </div>
              </>
            )}
          </section>
        </section>
      </Modal>
    </div>
  )
}

export default AlertCheckout
